import { API } from '../Api';

export interface WithdrawRequest {
  id: string;
  userId: string;
  amount: number;
  receivedAmount: number;
  bankName: string;
  bankAccountNumber: string;
  bankAccountName: string;
  status: 'Submitted' | 'Done' | 'Rejected';
  note: string;
  createdAt: string;
  // Bảng 2 (Log Rút) — kế toán/đối soát thuế (BE trả về)
  ekycVerifiedName?: string | null;
  ekycIdNumber?: string | null;
  amountVnd?: number; // A
  platformFee?: number; // B
  personalIncomeTax?: number; // C
  netTransfer?: number; // D = A - B - C
  bankTransactionCode?: string | null;
}

interface GetWithdrawRequestsResponse {
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  items: WithdrawRequest[];
}

export const getWithdrawRequests = (params: {
  pageNumber: number;
  pageSize: number;
  status: string;
  fromDate?: string;
  toDate?: string;
}) => {
  return new Promise<GetWithdrawRequestsResponse>((resolve, reject) => {
    API.get('/v1/payment-service/transactions/withdraw-requests', { params })
      .then((res) => {
        resolve(res.data);
      })
      .catch(reject);
  });
};

// Xuất hồ sơ định danh + dòng tiền của 1 user (admin) để cung cấp cho cơ quan chức năng.
export const exportAuthorityDossier = (
  userId: string,
  totpCode: string,
  fromDate?: string,
  toDate?: string
) => {
  const params: Record<string, string> = { userId, totpCode };
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;
  return API.get('/v1/payment-service/transactions/authority-export', {
    params,
  }).then((res) => res.data);
};

// Xem CCCD đầy đủ 1 lệnh rút (admin) — BE giải mã + ghi log audit. Danh sách mặc định chỉ 4 số cuối.
export const revealWithdrawEkyc = (id: string, totpCode: string) => {
  return API.get<{ id: string; ekycIdNumber?: string; ekycVerifiedName?: string; bankAccountNumber?: string }>(
    `/v1/payment-service/transactions/withdraw-requests/${id}/ekyc`,
    { params: { totpCode } }
  ).then((res) => res.data);
};

export const updateWithdrawRequestStatus = (
  id: string,
  status: 'Done' | 'Rejected',
  bankTransactionCode?: string,
  totpCode?: string
) => {
  return new Promise<boolean>((resolve, reject) => {
    API.post('/v1/payment-service/transactions/withdraw-requests/status', {
      id,
      status,
      // Chỉ gửi khi admin nhập (lúc xác nhận đã chuyển khoản)
      ...(bankTransactionCode ? { bankTransactionCode } : {}),
      // Mã Authenticator (bắt buộc khi duyệt Done — step-up bảo mật)
      ...(totpCode ? { totpCode } : {}),
    })
      .then((res) => {
        resolve(true);
      })
      .catch(reject);
  });
};

// Báo cáo doanh thu–chi phí–lợi nhuận theo kỳ (Mô hình A: chỉ phí là doanh thu) — admin.
export interface RevenueReport {
  fromDate?: string | null;
  toDate?: string | null;
  topUpCount: number;
  topUpBaseVnd: number; // nợ phải trả (KHÔNG phải doanh thu)
  topUpGrossUpRevenue: number; // doanh thu phí nạp
  topUpVnpayCostEst: number; // chi phí VNPay ước tính
  withdrawCount: number;
  withdrawPlatformFee: number; // doanh thu phí rút (B)
  withdrawTaxWithheld: number; // thuế TNCN thu hộ (C)
  withdrawNetTransfer: number; // đã chi user (D)
  adRevenuePersonal: number; // doanh thu quảng cáo cá nhân
  adRevenueBusiness: number; // doanh thu quảng cáo doanh nghiệp
  cabPlusRevenue: number; // doanh thu Cab+ Pro
  membershipRevenue: number; // doanh thu hội viên kênh
  membershipSlotPayout: number; // chi trả lượt Cab+ cho creator (chi phí)
  aiRevenue: number; // doanh thu token AI
  affiliateRevenue: number; // doanh thu tiếp thị liên kết (phần Cab giữ)
  brandDealFeeRevenue: number; // I-3: doanh thu phí sàn Hợp tác creator (Brand Deal) — 10% giá deal
  serviceRevenue: number; // tổng doanh thu dịch vụ
  aiProviderCostEst: number; // chi phí nhà cung cấp AI (ước tính)
  creatorAdShareCost: number; // chia creator từ quảng cáo (CreatorAdRevenue) trong kỳ
  publisherShareCost: number; // chi phí chia cho publisher (Ads Network) — phần Cab thu hộ
  totalRevenue: number;
  totalVnpayCostEst: number;
  grossProfitEst: number;
  taxWithheldTotal: number;
  vnpayActualFeePercent: number;
  vnpayActualFixedFeeVnd: number;
  costIsEstimate: boolean;
}

export const getRevenueReport = (fromDate?: string, toDate?: string) => {
  const params: Record<string, string> = {};
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;
  return API.get<RevenueReport>('/v1/payment-service/transactions/revenue-report', {
    params,
  }).then((res) => res.data);
};

// Sổ giao dịch chi tiết (drill-down kế toán) — admin.
export interface LedgerEntry {
  id: string;
  createdAt: string;
  type: string;
  fromUserId: string;
  toUserId: string;
  spendAmount: number;
  receiveAmount: number;
  feeAmount: number;
  afterFromUserBalance: number;
  afterToUserBalance: number;
  message?: string | null;
  availableAt?: string | null;
  postId?: string | null;
}
export interface TransactionLedger {
  items: LedgerEntry[];
  totalCount: number;
  totalSpend: number;
  totalFee: number;
}
export interface LedgerFilter {
  type?: string;
  userId?: string;
  fromDate?: string;
  toDate?: string;
  minAmount?: number;
  maxAmount?: number;
  skip?: number;
  take?: number;
}
export const getTransactionLedger = (filter: LedgerFilter) => {
  const params: Record<string, string | number> = {};
  Object.entries(filter).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) params[k] = v as string | number;
  });
  return API.get<TransactionLedger>('/v1/payment-service/transactions/ledger', { params }).then((r) => r.data);
};

// Bảng 1 — Nhật ký Nạp Ca (đối soát VNPay) — admin.
export interface TopUpReportItem {
  ourTxnRef: string;
  vnpayTransactionNo: string;
  userId?: string | null;
  amountVnd: number;
  caAmount: number;
  gatewayFee: number;
  status: string;
  createdAt: string;
}

export interface TopUpReportPage {
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  items: TopUpReportItem[];
}

export const getTopUpReport = (
  fromDate?: string,
  toDate?: string,
  pageNumber = 1,
  pageSize = 20
) => {
  const params: Record<string, string | number> = { pageNumber, pageSize };
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;
  return API.get<TopUpReportPage>(
    '/v1/payment-service/transactions/topup-report',
    { params }
  ).then((res) => res.data);
};
